"use client";
import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

interface CresultcardFirstWordFloor {
  readonly first: ReactNode;
  readonly rest: ReactNode;
  readonly firstRef: RefObject<HTMLSpanElement | null>;
  readonly floor: number;
}

export function useCresultcardFirstWordFloor(
  label: ReactNode,
): CresultcardFirstWordFloor {
  const firstRef = useRef<HTMLSpanElement>(null);
  const [floor, setFloor] = useState(0);
  const match =
    typeof label === "string" ? /^(\S+)([\s\S]*)$/u.exec(label) : null;
  const first = typeof label !== "string" || match == null ? label : match[1];
  const rest = typeof label !== "string" ? null : match == null ? "" : match[2];

  useLayoutEffect(() => {
    const node = firstRef.current;
    if (node == null) {
      setFloor(0);
      return;
    }
    let active = true;
    const measure = (): void => {
      if (!active) return;
      const next = Math.ceil(node.getBoundingClientRect().width);
      setFloor((current) => (current === next ? current : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    if (document.fonts != null) {
      void document.fonts.ready.then(measure);
    }
    return () => {
      active = false;
      observer.disconnect();
    };
  }, [label]);

  return { first, rest, firstRef, floor };
}
